import React from "react";
import { Popover, Tag, Button, Popconfirm, Space } from "antd";
import {
  RiEditLine,
  RiDeleteBinLine,
  RiArrowLeftRightLine,
} from "react-icons/ri";
import {
  segmentLength,
  directionLabel,
  appliesToLabel,
  isSingleDirection,
  isProvisionalTripwireId,
} from "../utils/tripwireUtils";
import { getTripwireColor } from "../utils/tripwireColors";

// Level-1 focus popover for a 越線 tripwire, anchored at the line's midpoint
// (tripwireMidpointPx). Mirrors StationPopover/DraggableSymbol's popover
// look (small antd Popover, plain text rows, a couple of buttons) rather
// than inventing a new visual language. Unlike StationPopover it is NOT
// trigger="click" on its own anchor -- the line body itself owns click
// (level-0 vs level-1 disambiguation lives in TripwireOverlay), so this is
// always `open` and closes only when the caller clears `focusedId`
// (background click or Esc, handled in FloorPlanModal/index.js).
const TripwirePopover = ({
  tripwire,
  anchorPosition,
  placement = "top",
  onEdit,
  onDelete,
  onFlip,
}) => {
  if (!tripwire || !anchorPosition) return null;

  const [p1, p2] = tripwire.points;
  const lengthM = segmentLength(p1, p2);
  const color = getTripwireColor(tripwire.enabled);
  // Provisional (not-yet-confirmed-by-the-server) line: its create POST
  // hasn't resolved, so there is no real id yet for 編輯/刪除/翻轉 to PUT/
  // DELETE against -- disable them rather than let a click race the create
  // (useTripwires.update/remove hold the same guard defensively).
  const isProvisional = isProvisionalTripwireId(tripwire.id);
  const showFlip = isSingleDirection(tripwire.direction) && !!onFlip;

  return (
    <div
      style={{
        position: "absolute",
        left: anchorPosition.x,
        top: anchorPosition.y,
        width: 0,
        height: 0,
        pointerEvents: "none",
      }}
    >
      <Popover
        open
        trigger={[]}
        placement={placement}
        content={
          <div style={{ minWidth: 180 }}>
            <div style={{ fontWeight: "bold", marginBottom: 4 }}>
              {tripwire.name || "未命名越線"}
            </div>
            <div style={{ fontSize: 12, color: "#595959", lineHeight: 1.8 }}>
              <div>警報方向：{directionLabel(tripwire.direction)}</div>
              <div>適用對象：{appliesToLabel(tripwire.applies_to)}</div>
              <div>
                狀態：
                <Tag
                  color={tripwire.enabled ? "magenta" : "default"}
                  style={{ marginLeft: 4 }}
                >
                  {tripwire.enabled ? "啟用" : "停用"}
                </Tag>
              </div>
              <div>長度：{lengthM.toFixed(2)} m</div>
            </div>
            <Space style={{ marginTop: 8 }} wrap>
              <Button
                size="small"
                icon={<RiEditLine />}
                onClick={onEdit}
                disabled={isProvisional}
              >
                編輯
              </Button>
              {showFlip && (
                <Button
                  size="small"
                  icon={<RiArrowLeftRightLine />}
                  onClick={onFlip}
                  disabled={isProvisional}
                >
                  翻轉限制側
                </Button>
              )}
              <Popconfirm
                title="確認刪除此越線警報？"
                onConfirm={onDelete}
                okText="刪除"
                cancelText="取消"
                disabled={isProvisional}
              >
                <Button
                  size="small"
                  danger
                  icon={<RiDeleteBinLine />}
                  disabled={isProvisional}
                >
                  刪除
                </Button>
              </Popconfirm>
            </Space>
          </div>
        }
      >
        <div
          style={{
            width: 1,
            height: 1,
            backgroundColor: color,
            pointerEvents: "none",
          }}
        />
      </Popover>
    </div>
  );
};

export default TripwirePopover;
